import React, { useState } from 'react';

const ImportModal = ({ data, onClose, onConfirm }) => {
  const [currentStep, setCurrentStep] = useState('preview'); // preview, importing, complete
  const [progress, setProgress] = useState({
    total: 0,
    completed: 0,
    current: '',
    details: {
      boxContents: { status: 'pending', message: '' },
      expiryDates: { status: 'pending', message: '' },
      services: { status: 'pending', message: '' },
      refresh: { status: 'pending', message: '' }
    }
  });

  // Calculate data summary
  const summary = React.useMemo(() => {
    const uniqueBoxes = [...new Set(data.map(item => item.BOX_NUMBER))];
    const uniqueSkus = [...new Set(data.map(item => item.SKU))];
    const totalItems = data.reduce((sum, item) => sum + (parseInt(item.QTY) || 0), 0);
    
    // Count unique SKUs with expiry/services, not total rows
    const skusWithExpiry = [...new Set(data.filter(item => item.EXPIRY_DATE).map(item => item.SKU))];
    const skusWithServices = [...new Set(data.filter(item => item.SERVICES_ADDED).map(item => item.SKU))];
    
    return {
      boxes: uniqueBoxes.length,
      skus: uniqueSkus.length,
      totalItems,
      itemsWithExpiry: skusWithExpiry.length,
      itemsWithServices: skusWithServices.length,
      uniqueBoxes: uniqueBoxes.sort((a, b) => a - b),
      errors: []
    };
  }, [data]);

  const handleConfirm = async () => {
    setCurrentStep('importing');
    setProgress(prev => ({ ...prev, total: 4, completed: 0, current: 'Starting import...' }));

    try {
      // Create a custom onConfirm function that provides progress updates
      await onConfirm((step, message, isComplete = false) => {
        setProgress(prev => ({
          ...prev,
          completed: isComplete ? prev.completed + 1 : prev.completed,
          current: message,
          details: {
            ...prev.details,
            [step]: {
              status: isComplete ? 'complete' : 'processing',
              message: message
            }
          }
        }));
      });

      setCurrentStep('complete');
      
      // Auto-close after success message shows for 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      setCurrentStep('preview'); // Go back to preview on error
      setProgress(prev => ({
        ...prev,
        current: `Error: ${error.message}`,
        details: {
          ...prev.details,
          error: { status: 'error', message: error.message }
        }
      }));
      
      // Show error alert with detailed information
      if (error.message.includes('Duplicate entry detected') || error.message.includes('already has existing boxes')) {
        alert(`❌ Import Failed\n\n${error.message}\n\n💡 Solutions:\n• Check your Excel file for duplicate SKUs in the same box number\n• Use "Reset Box Contents" to clear existing data first\n• Ensure each SKU appears only once per box`);
      } else {
        alert(`❌ Import Failed\n\n${error.message}`);
      }
    }
  };

  const formatDate = (date) => {
    if (!isNaN(date) && typeof date === 'number') {
      return convertExcelDateToJSDate(date);
    } else if (!isNaN(Date.parse(date))) {
      return date;
    }
    return '';
  };

  const convertExcelDateToJSDate = (serial) => {
    const utc_days = Math.floor(serial - 25569);
    const date_info = new Date(utc_days * 86400 * 1000);
    const fractional_day = serial - Math.floor(serial) + 0.0000001;
    const total_seconds = Math.floor(86400 * fractional_day);
    const seconds = total_seconds % 60;
    const hours = Math.floor(total_seconds / 3600);
    const minutes = Math.floor(total_seconds / 60) % 60;
    return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds).toISOString().split('T')[0];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <div className="w-5 h-5 bg-green-500 text-white flex items-center justify-center text-xs font-bold">✓</div>;
      case 'processing':
        return <div className="w-5 h-5 bg-blue-500 text-white flex items-center justify-center text-xs animate-spin">⟳</div>;
      case 'error':
        return <div className="w-5 h-5 bg-red-500 text-white flex items-center justify-center text-xs font-bold">✗</div>;
      default:
        return <div className="w-5 h-5 bg-gray-300 border border-gray-400"></div>;
    }
  };

  const renderPreview = () => (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-300 pb-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Import Preview</h2>
          <p className="text-sm text-gray-600 mt-1">Review your Excel data before importing</p>
        </div>
        <button 
          onClick={onClose}
          className="w-8 h-8 bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 border border-gray-300 transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        <div className="bg-blue-50 border border-blue-200 p-3">
          <div className="text-xl font-bold text-blue-700">{summary.boxes}</div>
          <div className="text-xs text-blue-600 font-medium">Boxes</div>
        </div>
        <div className="bg-green-50 border border-green-200 p-3">
          <div className="text-xl font-bold text-green-700">{summary.skus}</div>
          <div className="text-xs text-green-600 font-medium">SKUs</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 p-3">
          <div className="text-xl font-bold text-purple-700">{summary.totalItems}</div>
          <div className="text-xs text-purple-600 font-medium">Total Items</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 p-3">
          <div className="text-xl font-bold text-orange-700">{summary.itemsWithExpiry}</div>
          <div className="text-xs text-orange-600 font-medium">With Expiry</div>
        </div>
        <div className="bg-pink-50 border border-pink-200 p-3">
          <div className="text-xl font-bold text-pink-700">{summary.itemsWithServices}</div>
          <div className="text-xs text-pink-600 font-medium">With Services</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="flex-1 border border-gray-300 bg-white min-h-0">
        <div className="overflow-auto h-full">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Box</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">SKU</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Qty</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Title</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Expiry</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Services</th>
                <th className="text-left p-2 font-medium text-gray-700 border-r border-gray-300">Weight</th>
                <th className="text-left p-2 font-medium text-gray-700">Dimensions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                  <td className="p-2 border-r border-gray-200 font-medium text-gray-900">#{row.BOX_NUMBER}</td>
                  <td className="p-2 border-r border-gray-200 font-mono text-xs">{row.SKU}</td>
                  <td className="p-2 border-r border-gray-200 text-center font-medium">{row.QTY}</td>
                  <td className="p-2 border-r border-gray-200 text-gray-700 max-w-xs truncate" title={row.TITLE}>
                    {row.TITLE}
                  </td>
                  <td className="p-2 border-r border-gray-200 text-xs">
                    {formatDate(row.EXPIRY_DATE) && (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 border border-orange-200 text-xs">
                        {formatDate(row.EXPIRY_DATE)}
                      </span>
                    )}
                  </td>
                  <td className="p-2 border-r border-gray-200 text-xs">
                    {row.SERVICES_ADDED && (
                      <span className="bg-pink-100 text-pink-700 px-2 py-1 border border-pink-200 text-xs">
                        {row.SERVICES_ADDED}
                      </span>
                    )}
                  </td>
                  <td className="p-2 border-r border-gray-200 text-xs">{row.WEIGHT}kg</td>
                  <td className="p-2 text-xs text-gray-600">
                    {row.LENGTH && `${row.LENGTH}×${row.WIDTH}×${row.HEIGHT}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-300">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 font-medium transition-colors"
        >
          Import {summary.totalItems} Items
        </button>
      </div>
    </div>
  );

  const renderProgress = () => (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-300 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Importing Data</h2>
        <p className="text-sm text-gray-600 mt-1">Processing your Excel import...</p>
      </div>

      {/* Progress Overview */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
          <span className="text-sm text-gray-600">{progress.completed}/{progress.total}</span>
        </div>
        <div className="w-full bg-gray-200 h-3 border border-gray-300">
          <div 
            className="bg-blue-600 h-full transition-all duration-300"
            style={{ width: `${(progress.completed / progress.total) * 100}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">{progress.current}</p>
      </div>

      {/* Step Details */}
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-3 p-3 border border-gray-300 bg-white">
          {getStatusIcon(progress.details.boxContents.status)}
          <div className="flex-1">
            <div className="font-medium text-gray-900">Box Contents</div>
            <div className="text-sm text-gray-600">{progress.details.boxContents.message || 'Import box contents and items'}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 border border-gray-300 bg-white">
          {getStatusIcon(progress.details.expiryDates.status)}
          <div className="flex-1">
            <div className="font-medium text-gray-900">Expiry Dates</div>
            <div className="text-sm text-gray-600">{progress.details.expiryDates.message || `Update ${summary.itemsWithExpiry} items with expiry dates`}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 border border-gray-300 bg-white">
          {getStatusIcon(progress.details.services.status)}
          <div className="flex-1">
            <div className="font-medium text-gray-900">Services</div>
            <div className="text-sm text-gray-600">{progress.details.services.message || `Apply services to ${summary.itemsWithServices} items`}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 border border-gray-300 bg-white">
          {getStatusIcon(progress.details.refresh.status)}
          <div className="flex-1">
            <div className="font-medium text-gray-900">Refresh Data</div>
            <div className="text-sm text-gray-600">{progress.details.refresh.message || 'Reload shipment data'}</div>
          </div>
        </div>

        {progress.details.error && (
          <div className="flex items-center gap-3 p-3 border border-red-300 bg-red-50">
            {getStatusIcon('error')}
            <div className="flex-1">
              <div className="font-medium text-red-900">Error</div>
              <div className="text-sm text-red-700">{progress.details.error.message}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <div className="w-16 h-16 bg-green-500 text-white flex items-center justify-center text-2xl font-bold mb-4 animate-pulse">
        ✓
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h2>
      <p className="text-gray-600 mb-4">Successfully imported {summary.totalItems} items into {summary.boxes} boxes</p>
      <div className="text-sm text-gray-500">Closing automatically...</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-gray-300 w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
        <div className="p-6 flex flex-col h-full min-h-0">
          {currentStep === 'preview' && renderPreview()}
          {currentStep === 'importing' && renderProgress()}
          {currentStep === 'complete' && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
